<template>
  <section class="relative isolate min-h-[calc(100vh-8rem)] overflow-hidden py-8">
    <div class="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
      <div class="grid h-full w-full grid-cols-3 gap-3 px-3 opacity-95 sm:grid-cols-4 sm:gap-4 sm:px-4 lg:grid-cols-6">
        <div
          v-for="(column, columnIndex) in imageColumns"
          :key="columnIndex"
          class="login-masonry-column"
          :class="{ 'hidden lg:grid': columnIndex > 3 }"
        >
          <img
            v-for="card in column"
            :key="card.src"
            class="h-full min-h-[9rem] w-full rounded-2xl object-cover shadow-xl shadow-stone-900/10"
            :src="card.src"
            :alt="card.alt"
            loading="lazy"
          />
        </div>
      </div>
      <div class="absolute inset-0 bg-gradient-to-b from-[#fdf7f0]/20 via-[#fdf7f0]/55 to-[#fdf7f0]" />
      <div class="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#fdf7f0] to-transparent" />
    </div>

    <div class="mx-auto flex min-h-[calc(100vh-12rem)] w-full max-w-6xl items-center justify-center px-4 sm:px-6 lg:px-8">
      <div class="grid w-full max-w-5xl items-center gap-8 lg:grid-cols-[1fr_26rem]">
        <div class="hidden max-w-xl text-slate-950 lg:block">
          <p class="mb-4 text-sm font-black uppercase tracking-[0.22em] text-rose-700">NetFlyr</p>
          <h1 class="m-0 text-5xl font-black leading-[0.95]">
            Discover the flyers that make your city move.
          </h1>
          <p class="mt-5 max-w-lg text-lg leading-8 text-slate-700">
            Sign in to save local finds, publish standout promotions, and keep your favorite events,
            offers, and services ready when you need them.
          </p>
          <div class="mt-8 grid max-w-lg grid-cols-3 gap-3">
            <div class="rounded-lg bg-white/80 p-4 shadow-lg shadow-stone-900/10 backdrop-blur">
              <strong class="block text-2xl font-black text-rose-700">Fresh</strong>
              <span class="text-sm font-bold text-slate-600">flyers near you</span>
            </div>
            <div class="rounded-lg bg-white/80 p-4 shadow-lg shadow-stone-900/10 backdrop-blur">
              <strong class="block text-2xl font-black text-blue-700">Fast</strong>
              <span class="text-sm font-bold text-slate-600">posting tools</span>
            </div>
            <div class="rounded-lg bg-white/80 p-4 shadow-lg shadow-stone-900/10 backdrop-blur">
              <strong class="block text-2xl font-black text-emerald-700">Saved</strong>
              <span class="text-sm font-bold text-slate-600">lists that sync</span>
            </div>
          </div>
        </div>

        <form
          class="mx-auto w-full max-w-[26rem] rounded-[2rem] border border-white/80 bg-white/95 p-6 shadow-2xl shadow-stone-900/20 backdrop-blur sm:p-8"
          @submit.prevent="login"
        >
          <div class="mb-7 text-center">
            <img class="mx-auto mb-4 h-12 w-auto" src="/logo.svg" alt="NetFlyr" />
            <h2 class="m-0 text-3xl font-black leading-tight text-slate-950">Welcome back</h2>
            <p class="mx-auto mt-2 max-w-xs text-sm leading-6 text-slate-600">
              Log in to manage your flyers, revisit saved picks, and explore what is happening nearby.
            </p>
          </div>

          <div class="grid gap-4">
            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              <span>Username</span>
              <input
                v-model="username"
                class="min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 font-normal text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-rose-500 focus:ring-4 focus:ring-rose-100"
                type="text"
                autocomplete="username"
                placeholder="@username"
                required
              />
            </label>

            <label class="grid gap-1.5 text-sm font-medium text-slate-700">
              <span>Password</span>
              <div class="relative">
                <input
                  v-model="password"
                  class="min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 pr-12 font-normal text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-rose-500 focus:ring-4 focus:ring-rose-100"
                  :type="showPassword ? 'text' : 'password'"
                  autocomplete="current-password"
                  placeholder="Enter your password"
                  required
                />
                <button
                  class="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-4 focus:ring-rose-100"
                  type="button"
                  :aria-label="showPassword ? 'Hide password' : 'Show password'"
                  :aria-pressed="showPassword"
                  @click="showPassword = !showPassword"
                >
                  <svg
                    v-if="showPassword"
                    class="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M10.7 5.1A10.9 10.9 0 0 1 12 5c5 0 9 4.5 10 7a13.5 13.5 0 0 1-3.2 4.5" />
                    <path d="M6.6 6.6A13 13 0 0 0 2 12c1 2.5 5 7 10 7a10.9 10.9 0 0 0 5.4-1.5" />
                    <path d="M2 2l20 20" />
                    <path d="M9.5 9.5a3.5 3.5 0 0 0 5 5" />
                    <path d="M14.5 9.5a3.5 3.5 0 0 0-5 5" />
                  </svg>
                  <svg
                    v-else
                    class="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
            </label>
          </div>

          <div class="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm">
            <label class="group inline-flex cursor-pointer items-center gap-2 font-black text-slate-600 transition hover:text-blue-700">
              <input v-model="rememberMe" class="peer sr-only" type="checkbox" />
              <span class="grid h-6 w-6 place-items-center rounded-lg border-2 border-slate-300 bg-white text-white shadow-sm transition peer-checked:border-blue-600 peer-checked:bg-blue-600 peer-focus:ring-4 peer-focus:ring-blue-100">
                <svg v-if="rememberMe" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="m5 12 4 4L19 6" />
                </svg>
              </span>
              <span>Remember me</span>
            </label>
            <router-link class="font-black text-blue-700 hover:text-blue-800" to="/forgot-password">
              Forgot password?
            </router-link>
          </div>

          <FlyerButton
            class="mt-5 w-full rounded-full bg-blue-600 hover:bg-blue-700 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-slate-300"
            type="submit"
            :disabled="isSubmitting"
          >
            {{ isPasswordSubmitting ? 'Logging in...' : 'Log in' }}
          </FlyerButton>

          <button
            class="mt-3 flex min-h-11 w-full items-center justify-center gap-3 rounded-full border border-slate-200 bg-white px-5 text-sm font-black text-slate-800 transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-200 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
            type="button"
            :disabled="isSubmitting"
            @click="loginWithGoogle"
          >
            <img class="h-5 w-5" src="@/assets/google-icon.png" alt="" />
            {{ isGoogleSubmitting ? 'Connecting Google...' : 'Continue with Google' }}
          </button>

          <p v-if="errorMessage" class="mt-3 rounded-2xl bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
            {{ errorMessage }}
          </p>

          <p class="mt-6 text-center text-sm font-bold leading-6 text-slate-600">
            New to NetFlyr?
            <router-link class="font-black text-blue-700 hover:text-blue-800" to="/register">
              Create a free account
            </router-link>
          </p>
        </form>
      </div>
    </div>
    <Teleport to="body">
      <div
        v-if="userTypeModalOpen"
        class="fixed inset-0 z-50 grid place-items-center bg-slate-950/55 px-4 py-6"
        role="dialog"
        aria-modal="true"
        aria-label="Choose account type"
        @click.self="cancelUserTypeChoice"
      >
        <section class="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl shadow-slate-950/20">
          <header class="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
            <div class="min-w-0">
              <h2 class="m-0 text-lg font-semibold text-slate-950">Choose account type</h2>
              <p class="m-0 mt-1 text-sm font-medium leading-5 text-slate-500">Select how you want to use NetFlyr.</p>
            </div>
            <button
              class="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-950"
              type="button"
              aria-label="Close account type"
              @click="cancelUserTypeChoice"
            >
              <i class="ri-close-line text-xl" aria-hidden="true"></i>
            </button>
          </header>

          <div class="grid gap-3 p-4">
            <button
              v-for="type in userTypes"
              :key="type.value"
              class="flex w-full items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:border-blue-200 hover:bg-blue-50/40 focus:outline-none focus:ring-4 focus:ring-blue-100"
              type="button"
              @click="resolveUserTypeChoice(type.value)"
            >
              <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" :class="type.iconClass" aria-hidden="true">
                <svg
                  v-if="type.value === 'business'"
                  class="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                  <rect width="20" height="14" x="2" y="6" rx="2" />
                </svg>
                <svg
                  v-else
                  class="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </span>
              <span class="min-w-0 flex-1">
                <span class="block text-base font-semibold leading-5 text-slate-950">{{ type.label }}</span>
                <span class="mt-1 block text-sm font-medium leading-5 text-slate-500">{{ type.description }}</span>
              </span>
              <span class="text-xl font-semibold text-slate-300" aria-hidden="true">›</span>
            </button>
          </div>
        </section>
      </div>
    </Teleport>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@vueuse/head'
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth'
import Swal from 'sweetalert2'
import { useSessionStore } from '@/stores/session'
import api from '@/services/api'
import { auth } from '@/services/firebase'
import FlyerButton from '@/components/common/FlyerButton.vue'

useHead({
  title: 'Login | NetFlyr',
  meta: [
    { name: 'description', content: 'Log in to NetFlyr to post local flyers, manage your account, save community posts, chat with nearby users, follow relevant updates, and discover events, jobs, rides, offers, and services.' },
    { name: 'keywords', content: 'NetFlyr login, sign in, local flyer account, community flyers, post flyers, saved flyers, flyer chat' }
  ]
})

const router = useRouter()
const route = useRoute()
const session = useSessionStore()
const username = ref('')
const password = ref('')
const showPassword = ref(false)
const rememberMe = ref(false)
const isPasswordSubmitting = ref(false)
const isGoogleSubmitting = ref(false)
const isSubmitting = computed(() => isPasswordSubmitting.value || isGoogleSubmitting.value)
const errorMessage = ref('')
const userTypeModalOpen = ref(false)
const userTypeChoiceResolver = ref(null)

const userTypes = [
  {
    value: 'business',
    label: 'Business',
    description: 'Post offers, events, jobs & more to your neighborhood',
    iconClass: 'bg-teal-50 text-teal-600'
  },
  {
    value: 'user',
    label: 'Community Member',
    description: "Discover local deals, share reviews, confirm what's real",
    iconClass: 'bg-orange-50 text-orange-600'
  }
]

const imageColumns = [
  [
    {
      src: 'https://dummyimage.com/360x520/f43f5e/ffffff&text=Weekend+Pop-Up',
      alt: 'Colorful NetFlyr placeholder for a weekend pop-up flyer'
    },
    {
      src: 'https://dummyimage.com/360x430/0ea5e9/ffffff&text=Music+Night',
      alt: 'Colorful NetFlyr placeholder for a music night flyer'
    }
  ],
  [
    {
      src: 'https://dummyimage.com/360x420/f59e0b/ffffff&text=Food+Festival',
      alt: 'Colorful NetFlyr placeholder for a food festival flyer'
    },
    {
      src: 'https://dummyimage.com/360x560/16a34a/ffffff&text=Local+Services',
      alt: 'Colorful NetFlyr placeholder for local service flyers'
    }
  ],
  [
    {
      src: 'https://dummyimage.com/360x590/8b5cf6/ffffff&text=Creative+Workshop',
      alt: 'Colorful NetFlyr placeholder for a creative workshop flyer'
    },
    {
      src: 'https://dummyimage.com/360x400/ec4899/ffffff&text=Sale+Alert',
      alt: 'Colorful NetFlyr placeholder for a sale alert flyer'
    }
  ],
  [
    {
      src: 'https://dummyimage.com/360x470/14b8a6/ffffff&text=Community+Meet',
      alt: 'Colorful NetFlyr placeholder for a community meet flyer'
    },
    {
      src: 'https://dummyimage.com/360x540/ef4444/ffffff&text=Grand+Opening',
      alt: 'Colorful NetFlyr placeholder for a grand opening flyer'
    }
  ],
  [
    {
      src: 'https://dummyimage.com/360x530/2563eb/ffffff&text=Fitness+Class',
      alt: 'Colorful NetFlyr placeholder for a fitness class flyer'
    },
    {
      src: 'https://dummyimage.com/360x410/a855f7/ffffff&text=Art+Market',
      alt: 'Colorful NetFlyr placeholder for an art market flyer'
    }
  ],
  [
    {
      src: 'https://dummyimage.com/360x430/f97316/ffffff&text=Hiring+Now',
      alt: 'Colorful NetFlyr placeholder for a hiring flyer'
    },
    {
      src: 'https://dummyimage.com/360x570/0891b2/ffffff&text=Neighborhood+News',
      alt: 'Colorful NetFlyr placeholder for neighborhood news'
    }
  ]
]

const getTimezonePayload = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  const offsetMinutes = -new Date().getTimezoneOffset()
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const hours = String(Math.floor(Math.abs(offsetMinutes) / 60)).padStart(1, '0')
  const minutes = String(Math.abs(offsetMinutes) % 60).padStart(2, '0')

  return {
    timezone,
    zone: `GMT${sign}${hours}:${minutes}`
  }
}

const extractUser = (payload) => {
  const user = payload?.data?.[0] || payload?.user || payload?.data || {}
  const fallbackUsername = username.value || user.username || user.name || 'user'
  return {
    ...user,
    id: user.id || user.user_id || 1,
    name: user.name || user.user_name || fallbackUsername,
    username: user.username || user.user_name || fallbackUsername,
    email: user.email || `${fallbackUsername}@netflyr.test`
  }
}

const apiLoginSucceeded = (payload) =>
  payload?.status === 'ok' && (payload?.tempstatus === undefined || String(payload.tempstatus) === '0') && !!payload?.data?.[0]

const getApiMessage = (payload, fallback) =>
  payload?.msg || payload?.errors?.[0]?.msg || payload?.message || fallback

const firstValue = (...values) => values.find((value) => String(value ?? '').trim() !== '')

const normalizeUserType = (value) => String(value ?? '').trim().toLowerCase()

const extractUserType = (payload = {}) => {
  const source = Array.isArray(payload?.data) ? payload.data[0] || {} : payload?.data || payload || {}
  return normalizeUserType(firstValue(source.user_type, source.userType, payload.user_type, payload.userType, ''))
}

const needsUserTypeChoice = (userType) => !userType || userType === 'pending'

const requestUserTypeChoice = () => {
  userTypeModalOpen.value = true
  return new Promise((resolve) => {
    userTypeChoiceResolver.value = resolve
  })
}

const resolveUserTypeChoice = (type) => {
  userTypeModalOpen.value = false
  const resolver = userTypeChoiceResolver.value
  userTypeChoiceResolver.value = null
  resolver?.(type)
}

const cancelUserTypeChoice = () => {
  userTypeModalOpen.value = false
  const resolver = userTypeChoiceResolver.value
  userTypeChoiceResolver.value = null
  resolver?.('')
}

const showLoginToast = (icon, title) =>
  Swal.fire({
    toast: true,
    position: 'top-end',
    icon,
    title,
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true
  })

const goToExplore = async (userPayload) => {
  session.setUser(extractUser(userPayload))
  await showLoginToast('success', getApiMessage(userPayload, 'Login successful'))
  router.push(typeof route.query.redirect === 'string' ? route.query.redirect : '/explore')
}

const login = async () => {
  if (isSubmitting.value) return

  isPasswordSubmitting.value = true
  errorMessage.value = ''

  try {
    const { data } = await api.login({
      username: username.value.trim(),
      password: password.value
    })

    if (!apiLoginSucceeded(data)) {
      const message = getApiMessage(data, 'Login failed. Check your username and password.')
      errorMessage.value = message
      await showLoginToast('error', message)
      return
    }

    await goToExplore(data)
  } catch (error) {
    const message = getApiMessage(error?.response?.data, 'Login failed. Check your username and password.')
    errorMessage.value = message
    await showLoginToast('error', message)
  } finally {
    isPasswordSubmitting.value = false
  }
}

const loginWithGoogle = async () => {
  if (isSubmitting.value) return

  isGoogleSubmitting.value = true
  errorMessage.value = ''

  try {
    const provider = new GoogleAuthProvider()
    provider.setCustomParameters({ prompt: 'select_account' })

    const result = await signInWithPopup(auth, provider)
    const googleUser = result.user
    const email = googleUser.email?.trim() || ''
    const { data: checkData } = await api.socialLoginCheck({ email })
    const checkedUserType = extractUserType(checkData)
    const selectedUserType = needsUserTypeChoice(checkedUserType)
      ? await requestUserTypeChoice()
      : checkedUserType

    if (needsUserTypeChoice(checkedUserType) && !selectedUserType) {
      errorMessage.value = 'Choose an account type to continue with Google.'
      return
    }

    const { data } = await api.socialLogin({
      name: googleUser.displayName?.trim() || googleUser.email?.split('@')[0] || 'Google User',
      email,
      ...getTimezonePayload(),
      login_type: 'google',
      ...(selectedUserType ? { user_type: selectedUserType } : {})
    })

    if (!apiLoginSucceeded(data)) {
      const message = getApiMessage(data, 'Google login failed.')
      errorMessage.value = message
      await showLoginToast('error', message)
      return
    }

    await goToExplore(data)
  } catch (error) {
    if (error?.code === 'auth/popup-closed-by-user') {
      errorMessage.value = 'Google login was cancelled.'
      await showLoginToast('info', errorMessage.value)
      return
    }

    const message = getApiMessage(error?.response?.data, error?.message || 'Google login failed.')
    errorMessage.value = message
    await showLoginToast('error', message)
  } finally {
    isGoogleSubmitting.value = false
  }
}
</script>

<style scoped>
.login-masonry-column {
  display: grid;
  min-height: 100%;
  grid-template-rows: 1.1fr 0.9fr;
  gap: 0.75rem;
}

.login-masonry-column:nth-child(even) {
  transform: translateY(2rem);
}

.login-masonry-column:nth-child(3n) {
  transform: translateY(-1.5rem);
}

@media (min-width: 640px) {
  .login-masonry-column {
    gap: 1rem;
  }
}

@media (max-width: 1023px) {
  .login-masonry-column:nth-child(even),
  .login-masonry-column:nth-child(3n) {
    transform: none;
  }
}
</style>
